import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie,
  Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Sparkles, Send, Loader2, BarChart3, PieChart as PieChartIcon,
  LineChart as LineChartIcon, Table, Save, Trash2, Clock,
  FileText, FileSpreadsheet, Download, RefreshCw, BookOpen, TableProperties
} from 'lucide-react';
import PivotApplicationsReport from '../components/reports/PivotApplicationsReport';

// Chart colors
const COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#3B82F6', '#EF4444', '#14B8A6'];

// Example queries for users
const EXAMPLE_QUERIES = [
  "Show me applications by status",
  "How many leads per source?",
  "Top 5 team members by applications",
  "Applications created per day this week",
  "Show all applications in counselor desk",
  "Conversion rate of leads to applications",
  "List applications pending for more than 7 days",
  "Show agents with most applications"
];

// Chart component based on type
const ReportChart = ({ data, chartType, config }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-muted-foreground">
        <p>No data to display</p>
      </div>
    );
  }

  const xField = config?.x_field || '_id';
  const yField = config?.y_field || 'count';

  // Prepare data for charts
  const chartData = data.map((item, index) => ({
    name: String(item[xField] || item._id || `Item ${index + 1}`),
    value: Number(item[yField] || item.count || item.value || 0),
    ...item
  }));

  if (chartType === 'bar') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis dataKey="name" tick={{ fill: 'currentColor' }} fontSize={12} />
          <YAxis tick={{ fill: 'currentColor' }} fontSize={12} />
          <Tooltip contentStyle={{ backgroundColor: 'var(--popover)', border: '1px solid var(--border)', borderRadius: '8px' }} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {chartData.map((_, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'line') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis dataKey="name" tick={{ fill: 'currentColor' }} fontSize={12} />
          <YAxis tick={{ fill: 'currentColor' }} fontSize={12} />
          <Tooltip contentStyle={{ backgroundColor: 'var(--popover)', border: '1px solid var(--border)', borderRadius: '8px' }} />
          <Line type="monotone" dataKey="value" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1' }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'pie') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
          >
            {chartData.map((_, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ backgroundColor: 'var(--popover)', border: '1px solid var(--border)', borderRadius: '8px' }} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // Default: table view
  return null;
};

// Data Table component
const DataTable = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No data to display
      </div>
    );
  }

  const columns = Object.keys(data[0]).filter(k => k !== '_id' || typeof data[0]._id === 'string');

  return (
    <div className="overflow-auto max-h-[400px] rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-muted">
          <tr>
            {columns.map(col => (
              <th key={col} className="text-left py-3 px-4 font-medium text-muted-foreground uppercase text-xs border-b border-border">
                {col.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
              {columns.map((col, j) => (
                <td key={j} className="py-3 px-4">
                  {typeof row[col] === 'object' ? JSON.stringify(row[col]) : String(row[col] ?? '-')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Template Card component
const TemplateCard = ({ template, onRun, onDelete }) => {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/50 transition-all group bg-card">
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-500/20 flex items-center justify-center">
        <BookOpen className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{template.name}</p>
        <p className="text-xs text-muted-foreground truncate">{template.query}</p>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="sm" onClick={() => onRun(template)}>
          <RefreshCw className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(template.id)}>
          <Trash2 className="w-4 h-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
};

// Main AI Report Generator Page
const ReportBuilderPage = () => {
  const { api } = useAuth();
  const inputRef = useRef(null);
  
  // State
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [chartType, setChartType] = useState('table');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api().get('/reports/ai-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const generateReport = async (queryText = query) => {
    if (!queryText.trim()) {
      toast.error('Please enter a query');
      return;
    }

    setIsLoading(true);
    setReport(null);

    try {
      const response = await api().post('/reports/ai-generate', { query: queryText });
      
      if (response.data.success) {
        setReport(response.data);
        setChartType(response.data.chart_type || 'table');
        toast.success(`Report generated: ${response.data.record_count} records found`);
      } else {
        toast.error(response.data.error || 'Failed to generate report');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      generateReport();
    }
  };

  const handleExampleClick = (example) => {
    setQuery(example);
    generateReport(example);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setIsSaving(true);
    try {
      await api().post('/reports/ai-templates', {
        name: templateName,
        query: report.query,
        chart_type: chartType,
        chart_config: report.chart_config || {}
      });
      toast.success('Template saved!');
      setShowSaveDialog(false);
      setTemplateName('');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const runTemplate = (template) => {
    setQuery(template.query);
    setChartType(template.chart_type || 'table');
    generateReport(template.query);
  };

  const deleteTemplate = async (templateId) => {
    try {
      await api().delete(`/reports/ai-templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const exportToCSV = () => {
    if (!report?.data?.length) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(report.data[0]).filter(k => k !== '_id');
    const csvRows = [headers.join(',')];
    report.data.forEach(row => {
      csvRows.push(headers.map(h => `"${row[h] || ''}"`).join(','));
    });
    
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${report.title?.replace(/\s+/g, '_') || 'report'}.csv`);
    toast.success('CSV exported!');
  };

  const exportToExcel = () => {
    if (!report?.data?.length) {
      toast.error('No data to export');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(report.data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${report.title?.replace(/\s+/g, '_') || 'report'}.xlsx`);
    toast.success('Excel exported!');
  };

  // Render example query buttons
  const exampleButtons = EXAMPLE_QUERIES.map((example, i) => (
    <button
      key={i}
      onClick={() => handleExampleClick(example)}
      className="px-3 py-1.5 text-xs rounded-full border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-muted-foreground hover:text-foreground"
    >
      {example}
    </button>
  ));

  // Render template list
  const templateList = templates.length === 0 ? (
    <p className="text-sm text-muted-foreground text-center py-4">No saved templates yet</p>
  ) : templates.map(template => (
    <TemplateCard
      key={template.id}
      template={template}
      onRun={runTemplate}
      onDelete={deleteTemplate}
    />
  ));

  // Chart type buttons
  const chartTypeButtons = [
    { type: 'table', icon: Table, label: 'Table' },
    { type: 'bar', icon: BarChart3, label: 'Bar' },
    { type: 'line', icon: LineChartIcon, label: 'Line' },
    { type: 'pie', icon: PieChartIcon, label: 'Pie' },
  ].map(({ type, icon: Icon, label }) => (
    <button
      key={type}
      onClick={() => setChartType(type)}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
        chartType === type
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border hover:border-primary/50'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-xs">{label}</span>
    </button>
  ));

  return (
    <div className="space-y-6" data-testid="report-builder-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Reporting</h1>
          <p className="text-muted-foreground">AI-driven and fixed-format reports — live from your CRM data</p>
        </div>
      </div>

      <Tabs defaultValue="ai" className="w-full">
        <TabsList>
          <TabsTrigger value="ai" data-testid="ai-reporting-tab">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Reporting
          </TabsTrigger>
          <TabsTrigger value="pivot" data-testid="pivot-reporting-tab">
            <TableProperties className="w-4 h-4 mr-2" />
            Pivot Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="mt-4">
          <div className="grid grid-cols-12 gap-6">
        {/* Main Content */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {/* Query Input */}
          <Card className="border-2 border-dashed border-primary/30">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
                  <Input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me anything... e.g., 'Show applications by status' or 'Top 5 counselors'"
                    className="pl-10 h-12 text-base"
                    disabled={isLoading}
                  />
                </div>
                <Button
                  onClick={() => generateReport()}
                  disabled={isLoading || !query.trim()}
                  className="h-12 px-6 bg-gradient-to-r from-amber-500 to-amber-500"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>

              {/* Example Queries */}
              <div className="mt-4">
                <p className="text-xs text-muted-foreground mb-2">Try these examples:</p>
                <div className="flex flex-wrap gap-2">
                  {exampleButtons}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loading State */}
          {isLoading && (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-500/20 flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-primary animate-pulse" />
                    </div>
                    <div className="absolute inset-0 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium">Analyzing your query...</p>
                    <p className="text-sm text-muted-foreground">AI is generating your report</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Report Results */}
          {report && !isLoading && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {report.title}
                      <Badge variant="outline" className="ml-2">
                        {report.record_count} records
                      </Badge>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{report.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowSaveDialog(true)}>
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportToCSV}>
                      <FileText className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportToExcel}>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Excel
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Chart Type Selector */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-muted-foreground">View as:</span>
                  {chartTypeButtons}
                </div>

                {/* Chart or Table */}
                {chartType !== 'table' && (
                  <div className="mb-6 p-4 rounded-lg bg-muted/30">
                    <ReportChart data={report.data} chartType={chartType} config={report.chart_config} />
                  </div>
                )}

                {/* Data Table */}
                <DataTable data={report.data} />

                {/* Metadata */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Generated at {new Date(report.generated_at).toLocaleString()}
                  </span>
                  <span>Collection: {report.collection}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Saved Templates */}
        <div className="col-span-12 lg:col-span-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Saved Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {templateList}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
          </div>
        </TabsContent>

        <TabsContent value="pivot" className="mt-4">
          <PivotApplicationsReport />
        </TabsContent>
      </Tabs>

      {/* Save Template Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Report Template</DialogTitle>
            <DialogDescription>Save this query for quick access later</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Template Name</label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Weekly Applications Report"
              />
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground mb-1">Query:</p>
              <p className="text-sm">{report?.query}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportBuilderPage;
